import Shader from './Shader/Shader';
import ViewerShader from './Shader/ViewerShader';
import Program from './Program';
import * as Uniform from './Uniform';
import { UniformType } from './Uniform';
import CanvasWrapper from '../Webgl/CanvasWrapper';
import { IS_SERVER } from '@af-app/config';
import { WEBGL_MAX_CHUNK_SIZE } from '../_config/Consts';
import Framebuffer from './Framebuffer';

export default class Webgl {
	protected static instances:{[name:string]:Webgl} = {}

	public static maxDimensions:[number, number] = [WEBGL_MAX_CHUNK_SIZE, WEBGL_MAX_CHUNK_SIZE];

	public static flush = () => {
		for(let name in Webgl.instances){
			if(!Webgl.instances.hasOwnProperty(name)) continue;
			Webgl.instances[name].delete();
		}
		Webgl.instances = {};
	}

	public static getInstance = (name:string, width?:number, height?:number, target?:CanvasWrapper) => {
		width = Math.min(width, Webgl.maxDimensions[0]);
		height = Math.min(height, Webgl.maxDimensions[1]);

		let instance = Webgl.instances["FBO_INSTANCE"];

		if(!instance && !target) throw new Error("Cannot initialize a webgl instance without a target.");
		
		if(!instance){
			instance = new Webgl(target);
			instance.name = "FBO_INSTANCE";
			Webgl.instances["FBO_INSTANCE"] = instance;
		}

		instance.currentInstance = name;

		if(name == "viewer") {
			instance.useFbo(null);
			if(width && height) instance.setSize(width, height);
		} else {
			if(!instance.fbos[name + "_a"] && (!width || !height)) throw new Error("Cannot initialize a webgl instance without dimensions.");
			if(!instance.fbos[name + "_b"] && (!width || !height)) throw new Error("Cannot initialize a webgl instance without dimensions.");

			if(!instance.fbos[name + "_a"]) instance.initFbo(name + "_a", width, height);
			if(!instance.fbos[name + "_b"]) instance.initFbo(name + "_b", width, height);

			if(width && height) instance.resizeInstance(name, width, height);
			instance.useFbo(instance.getNextFbo(name));
		}

		return instance;
	}

	static listInstances = () => {
		console.group("Webgl Instances");
		for(let name in Webgl.instances){
			if(!Webgl.instances.hasOwnProperty(name)) continue;
			let instance = Webgl.instances[name];
			console.groupCollapsed(name);
			console.log(instance);
			console.groupEnd();
		}
		console.groupEnd();
	}

	public currentFramebuffer:string;
	public currentInstance:string;
	public currentProgram:Program
	public fbos:{[key:string]: Framebuffer} = {};
	public gl:WebGLRenderingContext | WebGL2RenderingContext;
	public name:string;
	public programs:{[key:string]:Program} = {};
	
	protected canvas:CanvasWrapper;
	protected isContextLost:boolean = false;
	protected passes:string[] = ["", "_coords"];
	protected positionBuffer:WebGLBuffer;
	protected positionLocation:number;
	protected texcoordBuffer:WebGLBuffer;
	protected texcoordLocation:number;

	protected constructor(canvas:CanvasWrapper) {
		if(typeof window !== 'undefined') {
			window.gl = Webgl.instances;
		}
		this.init(canvas);
	}

	public getCanvas = () => this.canvas;

	private init = (canvas:CanvasWrapper) => {
		this.canvas = canvas;
		this.gl = canvas.context;
		canvas.webgl = this;

		if(!IS_SERVER) {
			this.gl.pixelStorei(this.gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);

			(this.canvas.canvas as HTMLCanvasElement).addEventListener('webglcontextlost', (e) => {
				e.preventDefault();
				this.isContextLost = true;
			}, false);

			(this.canvas.canvas as HTMLCanvasElement).addEventListener('webglcontextrestored', (e) => {
				this.reset();
			}, false);
		} else {
			this.passes = [""];
		}

		this.initBuffers();
	}

	public reset = () => {
		this.clearPrograms();
		this.clearFbos();
		this.currentFramebuffer = '';
		this.currentInstance = '';
		this.currentProgram = null;
		this.positionLocation = null;
		this.texcoordLocation = null;

		this.canvas.delete();

		this.canvas.initContext(this.canvas.width, this.canvas.height);
		CanvasWrapper.instances.push(this.canvas);

		this.init(this.canvas);

		this.isContextLost = false;
	}

	public createProgram = (key:string, shader:Shader) => {
		let gl = this.gl;
		let program = new Program(gl, shader);
		this.registerProgram(key, program);
	}

	public useProgram = (key:string) => {
		let program = this.programs.hasOwnProperty(key) ? this.programs[key] : this.programs["default"];

		if(this.currentProgram === program)
			return;

		let gl = this.gl;
		if(this.positionLocation != -1) gl.disableVertexAttribArray(this.positionLocation);
		if(this.texcoordLocation != -1) gl.disableVertexAttribArray(this.texcoordLocation);
		this.currentProgram = program;

		this.positionLocation = this.currentProgram.getAttribLocation(gl, this.currentProgram.shader.positionAttr);
		this.texcoordLocation = this.currentProgram.getAttribLocation(gl, this.currentProgram.shader.texcoordAttr);

		if(this.positionLocation != -1) gl.enableVertexAttribArray(this.positionLocation);
		if(this.texcoordLocation != -1) gl.enableVertexAttribArray(this.texcoordLocation);
	}

	public setSize = (width:number, height:number) => {
		let canvas = this.getCanvas();
		width = Math.round(width);
		height = Math.round(height);
		if(canvas.width == width && canvas.height == height) return;
		canvas.resize(width, height);
	}

	public render = (inputFbo?:string, clear:boolean = true) => {
		if(this.isContextLost) return;

		let gl = this.gl;

		gl.useProgram(this.currentProgram.core);

		let width = this.canvas.width;
		let height = this.canvas.height;

		let currentFbo = this.currentFramebuffer;

		if(currentFbo){
			width = this.fbos[currentFbo].width;
			height = this.fbos[currentFbo].height;
		}

		for(let pass in this.passes) {
			if(!this.passes.hasOwnProperty(pass)) continue;

			if(!currentFbo && this.passes[pass] != ""){
				continue;
			}

			this.setupPass(this.passes[pass]);

			if(currentFbo){
				this.useFbo(currentFbo + this.passes[pass]);
				this.fbos[currentFbo + this.passes[pass]].drawCount++;
			}

			let input = (inputFbo || this.getNextFbo()) + this.passes[pass];

			let texture = this.fbos[input].texture;
			this.currentProgram.bindTexture("u_input", texture);

			if(clear) {
				gl.clearColor(0, 0, 0, 0);
				gl.clear(gl.COLOR_BUFFER_BIT);
			}

			if(false) {
				// Unused code trying to chunk in webgl itself, not really working, not really necessary.
				//this.setBuffer(Webgl.maxDimensions[1] / width, Webgl.maxDimensions[2] / height);
				//gl.viewport(chunk[0], chunk[1], chunk[2], chunk[3]);
				this.renderPass();
			} else {
				if(currentFbo){
					gl.viewport(0, 0, this.fbos[currentFbo].width, this.fbos[currentFbo].height);
				} else {
					gl.viewport(0, 0, this.canvas.width, this.canvas.height);
				}
				this.renderPass();
			}

			this.useFbo(currentFbo);
		}
	}

	protected renderPass = () => {
		let gl = this.gl;
		
		if(this.positionLocation != -1){
			gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);
			gl.vertexAttribPointer(this.positionLocation, 2, gl.FLOAT, false, 0, 0);
		}

		if(this.texcoordLocation != -1){
			gl.bindBuffer(gl.ARRAY_BUFFER, this.texcoordBuffer);
			gl.vertexAttribPointer(this.texcoordLocation, 2, gl.FLOAT, false, 0, 0);
		}

		for (let propname in this.currentProgram.shader) {
			if(!this.currentProgram.shader.hasOwnProperty(propname)) continue;
			let prop = this.currentProgram.shader[propname];
			if(!prop || !prop.hasOwnProperty('isUniform')) continue;
			if(!prop.isUniform()) continue;

			//Needs to append [0] to uniform  array names on serverside
			if(IS_SERVER && prop.isUniformArray()) {
				propname = propname+"[0]";
			}

			(prop as Uniform.Uniform).set(gl, this.currentProgram.getUniformLocation(gl, propname));
		}

		for (let texturename in this.currentProgram.textures) {
			let texture = this.currentProgram.textures[texturename];
			gl.uniform1i(this.currentProgram.getUniformLocation(gl, texturename), texture.index);
		}

		gl.drawArrays(gl.TRIANGLES, 0, 6);
	}

	protected setupPass = (_passname:string) => {
		switch (_passname) {
			case "_coords":
				this.currentProgram.shader.u_outputCoords = new Uniform.Uniform1i(1);
				break;
			
			default:
				this.currentProgram.shader.u_outputCoords = new Uniform.Uniform1i(0);
				break;
		}
	}

	public clear = () => {
		if(this.isContextLost) return;

		let gl = this.gl;
		let currentFbo = this.currentFramebuffer;
		for(let fbo in this.fbos){
			if(!this.fbos.hasOwnProperty(fbo)) continue;
			this.useFbo(fbo);
			this.fbos[this.currentFramebuffer].drawCount = 0;
			gl.viewport(0, 0, this.canvas.width, this.canvas.height);
			gl.clearColor(0, 0, 0, 0);
			gl.clear(gl.COLOR_BUFFER_BIT);
		}
		this.useFbo(currentFbo);
	}

	public clearInstance = (instance:string) => {
		if(this.isContextLost) return;

		let gl = this.gl;

		let currentfbo = this.currentFramebuffer;

		for(let pass in this.passes){
			if(!this.passes.hasOwnProperty(pass)) continue;
			this.useFbo(instance + "_a" + this.passes[pass]);
			this.fbos[this.currentFramebuffer].drawCount = 0;
			gl.viewport(0, 0, this.canvas.width, this.canvas.height);
			gl.clearColor(0, 0, 0, 0);
			gl.clear(gl.COLOR_BUFFER_BIT);

			this.useFbo(instance + "_b" + this.passes[pass]);
			this.fbos[this.currentFramebuffer].drawCount = 0;
			gl.viewport(0, 0, this.canvas.width, this.canvas.height);
			gl.clearColor(0, 0, 0, 0);
			gl.clear(gl.COLOR_BUFFER_BIT);
		}

		this.useFbo(currentfbo);
	}

	public resizeInstance = (name:string, width:number, height:number) => {
		for(let pass in this.passes){
			if(!this.passes.hasOwnProperty(pass)) continue;
			if( this.fbos[name + "_a" + this.passes[pass]].width == width || this.fbos[name + "_a" + this.passes[pass]].height == height ||
				this.fbos[name + "_b" + this.passes[pass]].width == width || this.fbos[name + "_b" + this.passes[pass]].height == height
			) return;

			if(this.fbos.hasOwnProperty(name + "_a" + this.passes[pass])){
				this.fbos[name + "_a" + this.passes[pass]].delete();
				this.initFbo(name + "_a", width, height);
			}

			if(this.fbos.hasOwnProperty(name + "_b" + this.passes[pass])){
				this.fbos[name + "_b" + this.passes[pass]].delete();
				this.initFbo(name + "_b", width, height);
			}
		}
	}

	private registerProgram = (key:string, program:Program) => {
		this.programs[key] = program;
	}

	private initBuffers = () => {
		let gl = this.gl;
		this.positionBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, this.positionBuffer);

		if(IS_SERVER || true) {
			gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
				-1.0, -1.0,
				 1.0, -1.0,
				-1.0,  1.0,
				-1.0,  1.0,
				 1.0, -1.0,
				 1.0,  1.0,
			]), gl.STATIC_DRAW);
		} else {
			gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
				-1.0, 1.0,
				1.0, 1.0,
				-1.0, -1.0,
				-1.0, -1.0,
				1.0, 1.0,
				1.0, -1.0,
			]), gl.STATIC_DRAW);
		}

		this.texcoordBuffer = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, this.texcoordBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
			0.0, 0.0,
			1.0, 0.0,
			0.0, 1.0,
			0.0, 1.0,
			1.0, 0.0,
			1.0, 1.0,
		]), gl.STATIC_DRAW);
	}

	private setBuffer = (width: number, height: number) => {
		let gl = this.gl;
		gl.bindBuffer(gl.ARRAY_BUFFER, this.texcoordBuffer);
		gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
			0.0, 0.0,
			width, 0.0,
			0.0, height,
			0.0, height,
			width, 0.0,
			width, height,
		]), gl.STATIC_DRAW);
	}

	public getPreviousFbo = (instance?: string) => {
		if(!instance) instance = this.currentInstance;

		return this.fbos[instance + "_b"].drawCount <= this.fbos[instance + "_a"].drawCount ? 
		instance + "_a" : instance + "_b";
	}

	public getNextFbo = (instance?: string) => {
		if(!instance) instance = this.currentInstance;

		return this.fbos[instance + "_b"].drawCount > this.fbos[instance + "_a"].drawCount ? 
		instance + "_a" : instance + "_b";
	}

	public useFbo = (name:string) => {
		let gl = this.gl;
		this.currentFramebuffer = name;
		if(name) {
			gl.bindFramebuffer(gl.FRAMEBUFFER, this.fbos[this.currentFramebuffer].core);
		} else {
			gl.bindFramebuffer(gl.FRAMEBUFFER, null);
		}
		gl.viewport(0, 0, this.canvas.width, this.canvas.height);
	}

	private initFbo = (name:string, width: number, height: number) => {
		for(let pass in this.passes){
			if(!this.passes.hasOwnProperty(pass)) continue;
			let passname = name + this.passes[pass];
			this.fbos[passname] = new Framebuffer(passname, width, height, this);
		}
	}

	public clearFbos = () => {
		for(let name in this.fbos){
			if(!this.fbos.hasOwnProperty(name)) continue;
			this.fbos[name].delete();
			delete this.fbos[name];
		}

		this.fbos = {};
	}

	public clearPrograms = () => {
		for(let program in this.programs){
			if(!this.programs.hasOwnProperty(program)) continue;
			this.programs[program].delete();
		}

		this.programs = {};
	}

	public delete = () => {
		this.clearPrograms();
		this.clearFbos();

		this.gl.deleteBuffer(this.positionBuffer);
		this.gl.deleteBuffer(this.texcoordBuffer);

		this.canvas.delete();
		
		delete this.canvas;
		delete Webgl.instances[this.name];
	}

	private getChunkRect = (index:number) => {
		let chunkPosition = this.getChunkPositionFromIndex(index);

		let dimensions = this.getChunkResolution();

		let width = this.canvas.width;
		let height = this.canvas.height;

		let currentFbo = this.currentFramebuffer;

		if(currentFbo){
			width = this.fbos[currentFbo].width;
			height = this.fbos[currentFbo].height;
		}

		return [
			chunkPosition[0] * Webgl.maxDimensions[0],
			chunkPosition[1] * Webgl.maxDimensions[1],
			chunkPosition[0] + 1 == dimensions[0] ? width - (chunkPosition[0] * Webgl.maxDimensions[0]) : Webgl.maxDimensions[0],
			chunkPosition[1] + 1 == dimensions[1] ? height - (chunkPosition[1] * Webgl.maxDimensions[1]) : Webgl.maxDimensions[1]
		];
	}

	private getChunkPositionFromIndex = (index:number) => {
		let chunkResolution = this.getChunkResolution();
		return [index % chunkResolution[0], Math.floor(index / chunkResolution[0])];
	}

	private getIndexFromChunkPosition = (x:number, y:number) => y * this.getChunkResolution()[0] + x;

	private getChunkResolution = () => {
		let width = this.currentFramebuffer ? this.fbos[this.currentFramebuffer].width : this.canvas.width;
		let height = this.currentFramebuffer ? this.fbos[this.currentFramebuffer].height : this.canvas.height;
		return [Math.ceil(width / Webgl.maxDimensions[0]), Math.ceil(height / Webgl.maxDimensions[1])];
	}

	private getChunkCount = () => {
		let chunkResolution = this.getChunkResolution();
		return chunkResolution[0] * chunkResolution[1];
	}
};